import { IOption, Input, MAX_PHONE_DIGITS, ReactDropdown, fetchCountryCodeFromPhone, validatePhoneNumber } from '@storybook';
import {
	ChangeEvent,
	FC,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import countries from 'json/country-codes.json';
import { CountryCode } from 'components';
import { InviteUserFromMessages, ROLE, message, validateEmail } from 'constant';
import {
	EditModal,
	FundsState,
	InviteDetailsState,
	InviteEmailInputState,
	InviteFirstNameInputState,
	InviteLastNameInputState,
	InvitePhoneInputState,
	InviteRoleState,
	LoginPhoneNumberState,
	UserRoles,
	UserRolesState,
	loginState,
	phoneNumberDraftState,
} from 'global-stores';
import { SubAccountListState } from 'views/sub-account/hooks';
import classNames from 'classnames';
import { PermissionTag } from './permission-tag';

/**
 * Funds and Sub Account are react-select, styled INLINE — so no stylesheet,
 * dark or otherwise, can reach them, and every literal below was a copy of the
 * LIGHT token value it is now written as: #f5f8ff is `--color-input-background`,
 * rgba(215, 223, 245, 1) is `--color-input-border-light`, rgba(47, 50, 61, 1) is
 * `--color-text-90-light`. Written as literals they stayed white while the
 * `@storybook` Input beside them (which reads the same tokens through scss)
 * followed the theme — two white boxes in a dark form.
 *
 * Tokens resolve to the identical values in light, and both dark scopes
 * re-point them: the panel-wide `:root[data-theme='dark']` in main.scss and the
 * sv2 `[data-sv2-theme='dark'] .react-modal_userRole` block in
 * design-system/tokens.scss.
 *
 * react-select's own defaults have to be named explicitly, because a colour it
 * sets itself is not inherited from the control: `singleValue` and
 * `multiValueLabel` both default to near-black (#333) and would vanish on a
 * dark control the moment the surface followed the theme.
 */
const createStyle = {
	control: (styles: { [key: string]: number | string }, state: { isFocused: boolean }) => ({
		...styles,
		backgroundColor: 'var(--color-input-background)',
		minHeight: 52,
		border: state.isFocused ? '1px solid var(--color-primary-light)' : '1px solid var(--color-input-border-light)',
		borderRadius: 8,
		fontWeight: '500',
		color: 'var(--color-text-90-light)',
		boxShadow: 'none',
		'&:hover': {
			cursor: 'pointer',
			/* was the literal 'f5f8ff' — no '#', so the declaration was invalid
			   and dropped; the resting border colour is what it meant. */
			borderColor: state.isFocused ? 'var(--color-primary-light)' : 'var(--color-input-border-light)',
		},
		'&:focus': {
			borderColor: 'red',
		},
	}),
	placeholder: (styles: { [key: string]: any }) => ({
		...styles,
		paddingLeft: 7, 
		fontWeight: '400',
		marginLeft: 0,
		marginRight: 0,
		fontSize: 16,
	}),
	singleValue: (styles: { [key: string]: any }) => ({
		...styles,
		color: 'var(--color-text-90-light)',
	}),
	menu: (styles: { [key: string]: any }) => ({
		...styles,
		background: 'var(--color-input-background)',
		borderRadius: 8,
		border: '1px solid var(--color-input-border-light)',
	}),
	menuList: (styles: any) => ({
		...styles,
		maxHeight: 190,
	}),
	multiValue: (styles: { [key: string]: any }) => ({
		...styles,
		backgroundColor: 'var(--color-button-bg-light-blue)',
		color: 'var(--color-text-90-light)',
		borderRadius: 50,
		paddingLeft: 8,
		paddingRight: 8,
		paddingTop: 4,
		paddingBottom: 4,
		margin: 4,
	}),
	multiValueLabel: (styles: { [key: string]: any }) => ({
		...styles,
		color: 'var(--color-text-90-light)',
	}),
	multiValueRemove: (styles: any) => ({
		...styles,
		color: 'rgba(163, 177, 214, 1)',
		borderRadius: 50,
		marginLeft: 2,
		fontSize: 12,
	}),
	option: (styles: { [key: string]: any }) => {
		return {
			...styles,
			backgroundColor: 'var(--color-input-background)',
			color: 'var(--color-text-90-light)',
			height: 48,
			cursor: 'pointer',
			paddingTop: 12,
			paddingBottom: 12,
			borderBottom: '1px solid var(--color-input-border-light)',
			fontWeight: '500',
			'&:hover': {
				color: '#fff',
				backgroundColor: '#3c65d6',
			},
			'&:last-child': {
				borderBottom: 'none',
			},
		};
	},
};
interface IInviteUserFrom {
	isDisableRole?: boolean;
	disableSubAccount?: boolean;
}

export const InviteUserFrom: FC<IInviteUserFrom> = ({
	isDisableRole = false,
	disableSubAccount = false,
}) => {
	const [inviteFunds, setInviteFunds] = useState<any>([]);
	const [showFirstNameError, setShowFirstNameError] = useRecoilState(
		InviteFirstNameInputState
	);
	const [showPhoneError, setShowPhoneError] = useRecoilState(
		InvitePhoneInputState
	);
	const [userPhoneNumber, setLoginPhoneNumber] = useRecoilState(
		LoginPhoneNumberState
	);
	const [phoneNumberDraft, setPhoneNumberDraft] = useRecoilState(phoneNumberDraftState);
	const [countryCode, setCountry] = useRecoilState(CountryCode);
	const [showLastNameError, setShowLastNameError] = useRecoilState(
		InviteLastNameInputState
	);
	const [showInviteEmailError, setShowInviteEmailError] = useRecoilState(
		InviteEmailInputState
	);
	const [showInviteRoleError, setShowInviteRoleError] = useRecoilState(
		InviteRoleState
	);

	const [isOpenDropdownModal, setIsOpenDropdownModal] = useState(false);

	const subAccountRows = useRecoilValue(SubAccountListState);
	const userRoles = useRecoilValue(UserRolesState);

	/* SUB-ACCOUNT INVITES REJECT A ROLE AFTER IT IS PICKED, SILENTLY.
	 *
	 * `handleSelectDropdown` below already encodes the real rule — a role
	 * without an onboarding subscription cannot be assigned in a sub-account
	 * invite (`disableSubAccount` is the flag for exactly this flow, set by
	 * `onInviteRole` when "Invite user" is opened from a specific sub-account
	 * row) — and sets `showInviteRoleError` to explain it. The message exists
	 * (`InviteUserFromMessages.roleVerification`, rendered right under this
	 * dropdown), but nothing stops an ineligible role from being selected in
	 * the first place: every role in `userRoles` is offered regardless, the
	 * Review button simply stays disabled afterward with no other sign
	 * anything is wrong, and a user who has already closed the dropdown has
	 * no reason to look for a one-line message below a field they are done
	 * with. That is the exact shape of SIM ticket "Review button stays
	 * disabled after all fields filled" — every field WAS filled; the one
	 * that silently failed was this one.
	 *
	 * Filtering here removes the invalid choice instead of reacting to it:
	 * nothing ineligible is ever offered, so there is nothing to explain
	 * after the fact. `handleSelectDropdown`'s own check stays as it was —
	 * harmless once this list is already filtered, and the real guard if
	 * this ever renders from `userRoles` directly again. */
	const eligibleRoles = useMemo(
		() =>
			disableSubAccount
				? (userRoles ?? []).filter(role =>
						role?.services?.includes('onboarding')
					)
				: userRoles,
		[userRoles, disableSubAccount]
	);

	const [changeFirstNameError, setChangeFirstNameError] = useState(false);
	const [changeLastNameError, setChangeLastNameError] = useState(false);
	const [changeEmailError, setChangeEmailError] = useState(false);

	const edit = useRecoilValue(EditModal);

	const funds = useRecoilValue(FundsState);
	const [invites, setInvites] = useRecoilState(InviteDetailsState);
	const inviteFundsEdit = useRecoilValue<any>(InviteDetailsState);

	const [selectedRoles, setSelectedRole] = useState<string>();

	const { role: loginUserRole } = useRecoilValue(loginState);
	const { OWNER } = useMemo(() => ROLE, []);

	const {
		VALID_NAME_MESSAGE,
		EMAIL_REQUIRED_MESSAGE,
		VALID_NO_SPECIAL_NUMBER_MESSAGE,
		EMAIL_NOT_VALID_MESSAGE,
	} = useMemo(() => message, []);

	const onChangeInput = useCallback(
		(name: string, e: ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			const nameRegex = /^[a-zA-Z0-9]+$/;
			if (name === 'firstName') {
				if (!nameRegex.test(value)) {
					setChangeFirstNameError(true);
					setShowFirstNameError(true);
				}
				if (value.length < 3) {
					setChangeFirstNameError(false);
					setShowFirstNameError(true);
				}
				if (nameRegex.test(value) && value.length > 2) {
					setChangeFirstNameError(false);
					setShowFirstNameError(false);
				}
			}
			if (name === 'lastName') {
				if (!nameRegex.test(value)) {
					setChangeLastNameError(true);
					setShowLastNameError(true);
				}
				if (value.length < 3) {
					setChangeLastNameError(false);
					setShowLastNameError(true);
				}
				if (nameRegex.test(value) && value.length > 2) {
					setChangeLastNameError(false);
					setShowLastNameError(false);
				}
			}
			if (name === 'email') {
				if (!validateEmail(value)) {
					setChangeEmailError(true);
					setShowInviteEmailError(true);
				}
				if (value.length < 1) {
					setChangeEmailError(false);
					setShowInviteEmailError(true);
				}
				if (validateEmail(value) && value.length > 0) {
					setChangeEmailError(false);
					setShowInviteEmailError(false);
				}
			}

			setInvites(prev => {
				const prevState = JSON.parse(JSON.stringify(prev));
				prevState[name] = value;
				return { ...prevState };
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);


	useEffect(() => {
		const fundValues: IOption = [];
		funds.forEach(fund =>
			fundValues.push({ label: fund.name, value: fund.id })
		);
		if (fundValues.length) {
			setInviteFunds(fundValues);
		}
	}, [funds]);

	const handleChange = useCallback(
		(e: IOption, name: string) => {
			setInvites((prev: IOption) => {
				const prevState = { ...prev, [name]: e };
				return { ...prevState };
			});
		},
		[setInvites]
	);

	// Validate the entered number against the selected country's metadata when
	// the field loses focus. Length limits (lower AND upper) come from
	// libphonenumber-js per country — nothing is hardcoded to 12 digits.
	const handlePhoneBlur = useCallback(() => {
		if (!phoneNumberDraft.trim()) {
			setShowPhoneError('');
			return;
		}
		const countryObj = countries.find(item => item.label === countryCode);
		const { isValid, message } = validatePhoneNumber(
			phoneNumberDraft,
			countryCode,
			countryObj?.value
		);
		setShowPhoneError(isValid ? '' : message);
	}, [phoneNumberDraft, countryCode, setShowPhoneError]);

	// New function to handle country code changes from the dropdown
	const onCountryChange = useCallback((selectedCountry: IOption) => {
		const countryCodeLabel = selectedCountry.label;
		setCountry(countryCodeLabel);
		setLoginPhoneNumber(prevState => ({
			...prevState,
			countryCode: countryCodeLabel,
		}));
		setShowPhoneError('');
	}, [setCountry, setLoginPhoneNumber, setShowPhoneError]);

	const onHandleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement> | any) => {
			const { value } = e.target;
			let digits = value.replace(/\D/g, '');
			let detectedCountryCode = countryCode;

			// Pasted / typed international number: peel off the dial code and
			// select that country. Detection runs on the raw value so a spaced
			// "+1 ..." resolves to US; the country can still be overridden below.
			if (value.trim().startsWith('+')) {
				const detectedCode = fetchCountryCodeFromPhone(value);
				if (detectedCode) {
					setCountry(detectedCode);
					detectedCountryCode = detectedCode;
					digits = value
						.slice(value.indexOf(detectedCode) + detectedCode.length)
						.replace(/\D/g, '');
				}
			}

			// Plain digits only, capped at the E.164 ceiling (15). No fixed 12
			// limit — per-country validity is checked on blur / submit.
			digits = digits.slice(0, MAX_PHONE_DIGITS);

			setLoginPhoneNumber(prevState => ({
				...prevState,
				phone: digits,
				countryCode: detectedCountryCode || prevState.countryCode,
			}));
			setPhoneNumberDraft(digits);
			setInvites((prev: IOption) => ({
				...prev,
				phone: digits,
				countryCode: detectedCountryCode || prev.countryCode,
			}));
			setShowPhoneError('');
		},
		[countryCode, setCountry, setLoginPhoneNumber, setPhoneNumberDraft, setInvites, setShowPhoneError]
	);


	// select after back of edit of invite form
	useEffect(() => {
		const role = userRoles?.find(el => el._id === invites?.role);
		setSelectedRole(role?.name ?? '');
	}, [userRoles, invites]);

	const subAccountOptions = useMemo(
		() => subAccountRows?.map(item => ({ label: item.name, value: item._id, status: item.approveStatus })),
		[subAccountRows]
	);

	const handleClickDropDown = useCallback(
		(e: React.MouseEvent<HTMLInputElement>) => {
			e.stopPropagation();
			setIsOpenDropdownModal(prev => !prev);
		},
		[]
	);

	const handleSelectDropdown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, userRole: UserRoles) => {
			e.stopPropagation();
			setSelectedRole(userRole?.name ?? '');
			setInvites((prev: IOption) => {
				const prevState = {
					...prev,
					role: userRole?._id ?? '',
				};
				if (!disableSubAccount) {
					prevState.subAccount = null
				}
				return { ...prevState };
			});
			if (disableSubAccount && userRole?._id) {
				const role = userRoles?.find(i => i._id === userRole?._id)?.services?.includes('onboarding')
				setShowInviteRoleError(!role)
			}
			setIsOpenDropdownModal(prev => !prev);
		},
		// eslint-disable-next-line
		[]
	);

	const isSubAccountValid = useMemo(() => {

		const currentBusinessMessage = `${InviteUserFromMessages.currentBussinessUser}`;
		if (subAccountOptions && subAccountOptions.length && inviteFundsEdit?.subAccount?.value) {
			const account = subAccountOptions.filter(item => inviteFundsEdit?.subAccount?.value === item.value && item.status === 'APPROVED')
			return !account.length ? `${InviteUserFromMessages.subAccountVerification}\n ${currentBusinessMessage}` : `${currentBusinessMessage}`
		}
		return inviteFundsEdit?.subAccount?.value ? `${currentBusinessMessage}` : ''
	}, [inviteFundsEdit?.subAccount, subAccountOptions]);

	/* EDIT SHOWS THE SAVED COUNTRY CODE, NOT THE PICKER'S DEFAULT (SIM-57).
	 *
	 * The picker reads the shared `CountryCode` atom, which defaults to '+1'
	 * and is only moved by a pick or a typed "+<code>". Neither edit handler
	 * (`handleEditSubAccountUser`, `handleEditUser`) touches it — they write the
	 * saved number to `LoginPhoneNumberState` — so a +91 user opened for edit
	 * drew the US flag beside their own number. The field is disabled in edit
	 * and the save sends only role and funds, so this is display-only; the
	 * atom is left alone so a fresh invite still starts on its default.
	 *
	 * `IPhoneNumber.countryCode` is typed `string | number`, while the picker
	 * takes a string and matches it against labels like "+91", so a bare 91
	 * is normalised to "+91" rather than passed through. */
	const displayedCountryCode = useMemo(() => {
		const saved = String(userPhoneNumber?.countryCode ?? '').trim();
		if (!edit || !saved) {
			return countryCode;
		}
		return saved.startsWith('+') ? saved : `+${saved}`;
	}, [edit, userPhoneNumber?.countryCode, countryCode]);

	const disableRoleClass = useMemo(() => {
		return classNames('invite_roles--container', {
			'invite_roles--container_disableRole': isDisableRole,
		});
	}, [isDisableRole]);

	return (
		<div className="invite invite__form">
			<div className="invite__name">
				<Input
					inputType="text"
					label="First Name"
					handleChange={e => onChangeInput('firstName', e)}
					placeholder="Enter first name"
					isError={showFirstNameError}
					errorMessage={
						changeFirstNameError
							? VALID_NO_SPECIAL_NUMBER_MESSAGE
							: VALID_NAME_MESSAGE
					}
					value={invites.firstName}
					disabled={edit}
					isRequired
				/>
				<Input
					inputType="text"
					label="Last Name"
					handleChange={e => onChangeInput('lastName', e)}
					placeholder="Enter last name"
					isError={showLastNameError}
					errorMessage={
						changeLastNameError
							? VALID_NO_SPECIAL_NUMBER_MESSAGE
							: VALID_NAME_MESSAGE
					}
					value={invites.lastName}
					disabled={edit}
					isRequired
				/>
			</div>
			<div className="invite__email">
				<Input
					handleChange={onHandleChange}
					inputType="text" //keep type text as number is not giving suggestions for auto fill will use regex to accept number
					value={phoneNumberDraft || userPhoneNumber.phone}
					placeholder={``}
					label="Mobile Number"
					handleBlur={handlePhoneBlur}
						handleChangeCountry={onCountryChange}
					isCountryCodeVisible={true}
					countryCode={displayedCountryCode}
					isError={!!showPhoneError}
					errorMessage={showPhoneError}
					autoComplete="tel"
					inputName="phone"
					disabled={edit}
					isRequired
				/>
			</div>
			<div className="invite__email">
				<Input
					inputType="text"
					placeholder="Enter email address"
					label="Email"
					value={invites.email}
					isError={showInviteEmailError}
					errorMessage={
						changeEmailError ? EMAIL_NOT_VALID_MESSAGE : EMAIL_REQUIRED_MESSAGE
					}
					handleChange={e => onChangeInput('email', e)}
					disabled={edit}
					isRequired
				/>
			</div>
			<div className="invite__funds">
				<div className="invite__funds__label">Funds</div>
				<ReactDropdown
					options={inviteFunds}
					optionsStyle={{
						'&:hover': { background: 'var(--color-hover-light-100)' },
					}}
					defaultValue={inviteFunds['label']}
					value={inviteFundsEdit?.funds}
					isMulti={true}
					closeMenuOnSelect
					handleChangeSelect={e => handleChange(e, 'funds')}
					createStyle={createStyle}
					placeholder="Select your funds"
				/>
			</div>

			<div className="invite_roles">
				<div className="invite__funds__label">
					Role <span className="input__isRequired"> *</span>
				</div>

				<div className={disableRoleClass}>
					<div className="dropdown--container" onClick={handleClickDropDown}>
						<div className="dropdown--container__value">
							{selectedRoles  ? (
								selectedRoles.charAt(0).toUpperCase() +
								selectedRoles.slice(1).toLowerCase()
							) : (
								isOpenDropdownModal &&
								<span className="dropdown--container--label">
									Select your role
								</span>
							)}
						</div>
						<div className="dropdown--container__icon">
							<i
								className={`ri-arrow-down-s-line ${
									isOpenDropdownModal && !isDisableRole && 'icon-active'
								}`}
							></i>
						</div>
					</div>
					{isOpenDropdownModal && !isDisableRole && (
						<div className="dropdown-modal">
							{(eligibleRoles ?? [])?.map((role: UserRoles) => (
								<div
									className="dropdown-modal--container"
									key={role?._id}
									onClick={(e: React.MouseEvent<HTMLDivElement>) =>
										handleSelectDropdown(e, role)
									}
								>
									<div className="dropdown-modal--container__header">
										<div className="dropdown-modal--container__header--label">
											{role?.name
												? role?.name.charAt(0).toUpperCase() +
												  role?.name.slice(1).toLowerCase()
												: 'NA'}
											<div className="dropdown-modal--container__circle"></div>
											<div className="dropdown-modal--container__price">
												${role?.price ?? 'NA'}
												<span className="dropdown-modal--container__price-per-month">
													/month
												</span>
											</div>
										</div>
										<div className="dropdown-modal--container__header--subTitle">
											{role?.description ? role?.description : 'NA'}
										</div>
									</div>
									<PermissionTag role={role} />
								</div>
							))}
							{/* Filtering out ineligible roles (above) must not trade one
							    silent dead end for another: a business with no
							    onboarding-enabled role at all would otherwise show an
							    empty dropdown and explain nothing, which is the exact
							    confusion this fix exists to remove. */}
							{disableSubAccount && !eligibleRoles?.length && (
								<div className="dropdown-modal--container dropdown-modal--container__empty">
									{InviteUserFromMessages.roleVerification}
								</div>
							)}
						</div>
					)}
				</div>
				{showInviteRoleError && (
					<span className="Error--message">
						{InviteUserFromMessages.roleVerification}
					</span>
				)}
			</div>
			{/* Don't have permission to see sub account on invite user label */}
			{loginUserRole === OWNER &&
				(isOpenDropdownModal && isDisableRole
					? userRoles
							?.find(i => i._id === invites.role)
							?.services?.includes('onboarding')
					: true) && (
					<div className="invite__funds">
						<div className="invite__funds__label">Sub account</div>
						<ReactDropdown
							options={subAccountOptions ?? []}
							optionsStyle={{
								'&:hover': { background: 'var(--color-hover-light-100)' },
							}}
							defaultValue={inviteFunds['label']}
							value={inviteFundsEdit?.subAccount}
							handleChangeSelect={e => handleChange(e, 'subAccount')}
							createStyle={createStyle}
							Isdisable={disableSubAccount}
							placeholder="Select your Sub Account"
							errorMessage={isSubAccountValid}
						/>
					</div>
				)}
			{/* <div className="invite__message">
				<Message
					text={'Note: Control the API flow by opting the developer option'}
					varient={'warning'}
				/>
			</div> */}
		</div>
	);
};
